import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  ListSubheader,
} from "@mui/material";
import TickIcon from "../../../assets/icons/tick-icon.svg";
import "./subscription-card.scss";
import {
  capitalizeFirstLetter,
  formatTitleCase,
  formatWithDollar,
} from "../../../utils/utils";
import { bgColors } from "../../../features/tenant/add-tenant/subscriptions-plans/subscription-plans";
import { handleButtonText } from "../../../features/tenant/add-tenant/add-new-tenet-helper";
import icons from "../../../assets/icons/icons";

interface CustomPlanCardProps {
  plans: any[];
  selectedPlan: any;
  activePlanId?: number;
  activePlan?: any;
  onChoosePlan?: (plan: any) => void;
  onCreate?: () => void;
  showCreateCustomPlanButton?: boolean;
  pendingPlan?: any;
}

const CustomPlanCard: React.FC<CustomPlanCardProps> = ({
  plans,
  selectedPlan,
  activePlan,
  onChoosePlan,
  onCreate,
  showCreateCustomPlanButton = true,
  pendingPlan,
}) => {
  const [displayedPlan, setDisplayPlan] = React.useState<any>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
      const val = searchInputRef.current.value;
      searchInputRef.current.value = "";
      searchInputRef.current.value = val;
    }
  }, [searchQuery, plans]);
  const selectRef = React.useRef<HTMLDivElement | null>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>(
    undefined
  );

  React.useEffect(() => {
    const computeWidth = () => {
      if (selectRef.current) {
        setMenuWidth(selectRef.current.clientWidth);
      }
    };
    computeWidth();
    window.addEventListener("resize", computeWidth);
    return () => window.removeEventListener("resize", computeWidth);
  }, []);

  const doesPlanExist = (plan: any) =>
    plan &&
    plans?.find((p) => p.id === plan.id && p.plan_type === plan.plan_type);

  React.useEffect(() => {
    let newPlan: any = null;
    const matchedSelected = doesPlanExist(selectedPlan);
    const matchedActive = doesPlanExist(activePlan);

    if (matchedSelected) {
      newPlan = matchedSelected;
    } else if (matchedActive) {
      newPlan = matchedActive;
    } else if (plans?.length > 0) {
      newPlan = plans[0];
    }

    setDisplayPlan(newPlan);
  }, [plans, selectedPlan, activePlan]);

  const isPending = React.useMemo(() => {
    return (
      pendingPlan &&
      pendingPlan?.id === displayedPlan?.id &&
      pendingPlan?.plan_type === displayedPlan?.plan_type
    );
  }, [pendingPlan, displayedPlan]);

  const isShowActiveBadge = React.useMemo(() => {
    return (
      displayedPlan?.id === activePlan?.id &&
      displayedPlan?.plan_type == activePlan?.plan_type
    );
  }, [displayedPlan, activePlan]);

  const hasPriceUpdate =
    isShowActiveBadge &&
    Number(activePlan?.price) !== Number(displayedPlan?.price);

  const color: string = displayedPlan?.theme_color || bgColors[0];

  return (
    <div>
      <Box
        className={
          hasPriceUpdate
            ? "subscription-card has-price-update"
            : "subscription-card"
        }
        sx={{
          position: "relative",
          width: "275.57px",
          minHeight: "603px",
          height: "auto",
          padding: "22px",
          pb: hasPriceUpdate ? "90px" : "40px",
          boxSizing: "border-box",
          background: "#ffffff",
          borderRadius: "16px",
          boxShadow:
            displayedPlan?.id === activePlan?.id ||
            selectedPlan?.id === displayedPlan?.id
              ? `0px 2px 8px ${color}`
              : 0,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Card
            sx={{
              borderRadius: "20px",
              textAlign: "center",
              background: color,
            }}
          >
            <CardContent>
              <Select
                ref={selectRef}
                value={displayedPlan?.id ?? ""}
                onOpen={() => setSearchQuery("")}
                onChange={(e) => {
                  const selectedId = Number(e.target.value);
                  const selected = plans.find((p) => p.id === selectedId);
                  if (selected) {
                    setDisplayPlan(selected);
                  }
                }}
                fullWidth
                displayEmpty
                variant="outlined"
                renderValue={() => (
                  <Box
                    sx={{
                      width: "100%",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: "22px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      textAlign: "left",
                      px: 1.5,
                      py: 0.5,
                    }}
                  >
                    {displayedPlan?.plan_name || ""}
                  </Box>
                )}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      width: menuWidth ? `${menuWidth}px` : undefined,
                    },
                  },
                }}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& .MuiSelect-select": {
                    py: 1,
                    px: 1.5,
                    textAlign: "left",
                    fontWeight: 700,
                    fontSize: "16px",
                  },
                }}
              >
                <ListSubheader
                  sx={{
                    position: "sticky",
                    top: 0,
                    bgcolor: "white",
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <TextField
                    placeholder="Search plans"
                    size="small"
                    fullWidth
                    inputRef={searchInputRef}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </ListSubheader>
                {(() => {
                  const filtered = (plans || []).filter((p) =>
                    p.plan_name
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase())
                  );
                  if (filtered.length === 0) {
                    return (
                      <MenuItem
                        disabled
                        tabIndex={-1}
                        sx={{
                          opacity: 0.7,
                          justifyContent: "center",
                          textAlign: "center",
                          pointerEvents: "none",
                        }}
                      >
                        No plan found
                      </MenuItem>
                    );
                  }
                  return filtered.map((p) => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                      selected={p.id === displayedPlan?.id}
                      sx={{
                        fontWeight: 600,
                        "&.Mui-selected": {
                          color: () => "white",
                          backgroundColor: () => "#726545",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: () => "#bbac85 !important",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          lineHeight: "20px",
                          textAlign: "left",
                        }}
                      >
                        {p.plan_name}
                      </Box>
                    </MenuItem>
                  ));
                })()}
              </Select>

              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: "#E4E4E4",
                  fontWeight: 400,
                  fontSize: "13px",
                  lineHeight: "30.46px",
                }}
              >
                {capitalizeFirstLetter(displayedPlan?.plan_type)} Plan
              </Typography>
            </CardContent>
          </Card>

          {/* PRICE CIRCLE */}
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: "50%",
              transform: "translateX(-50%)",
              width: "65.45px",
              height: "65.45px",
              borderRadius: "50%",
              backgroundColor: "#ffffff",
              color: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              fontWeight: 700,
              fontSize: displayedPlan?.price?.includes("$") ? "18.7px" : "12px",
              fontFamily: "Roboto",
            }}
          >
            {formatWithDollar(displayedPlan?.price)}
          </Box>

          {/* Divider line */}
          <Box
            sx={{
              position: "absolute",
              bottom: "-7%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              padding: "0px 10px",
            }}
          >
            <Box className="line left" sx={{ backgroundColor: color }} />
            <Box className="line right" sx={{ backgroundColor: color }} />
          </Box>
        </Box>

        <Typography
          variant="body1"
          align="center"
          color="#929292"
          sx={{
            fontWeight: 400,
            fontSize: "14px",
            marginTop: "35px",
            wordBreak: "break-all",
          }}
        >
          {displayedPlan?.suited_for}
        </Typography>

        {isShowActiveBadge && (
          <Box className="active-plan-wrapper">
            <Box className="active-plan-container">
              <Box className="active-plan-wrapper-dot"></Box>Active Plan
            </Box>
          </Box>
        )}
        {isPending && (
          <Box className="active-plan-wrapper">
            <Box className="active-plan-container pending-plan">
              <Box className="active-plan-wrapper-dot"></Box>Pending Plan
            </Box>
          </Box>
        )}
        <Box>
          <Box
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              marginTop: "30px",
              textAlign: "center",
            }}
          >
            Plan Inclusions
          </Box>

          <ul className="custom-list">
            {displayedPlan?.no_of_users && (
              <li>
                <img src={TickIcon} alt="tick" />
                {displayedPlan?.no_of_users > 0
                  ? displayedPlan?.no_of_users
                  : "Unlimited"}{" "}
                Users
              </li>
            )}
            {displayedPlan?.no_of_leads || displayedPlan?.no_of_contacts ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {displayedPlan?.no_of_leads
                  ? `${
                      displayedPlan?.no_of_leads > 0
                        ? displayedPlan?.no_of_leads
                        : "Unlimited"
                    } Leads`
                  : ""}{" "}
                {displayedPlan?.no_of_contacts
                  ? ` and ${displayedPlan?.no_of_contacts > 0 ? displayedPlan?.no_of_contacts : "Unlimited"}  Contacts`
                  : ""}
              </li>
            ) : (
              <></>
            )}
            {displayedPlan?.storage_limits && (
              <li>
                <img src={TickIcon} alt="tick" />
                {displayedPlan?.storage_limits} data storage limit
              </li>
            )}
            {displayedPlan?.api_calls_per_month ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {displayedPlan?.api_calls_per_month > 0
                  ? displayedPlan?.api_calls_per_month
                  : "Unlimited"}{" "}
                API calls per month
              </li>
            ) : (
              <></>
            )}
            {displayedPlan?.automation_rules_limit ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {displayedPlan?.automation_rules_limit > 0
                  ? displayedPlan?.automation_rules_limit
                  : "Unlimited"}{" "}
                Custom Automation Rules
              </li>
            ) : (
              <></>
            )}
            {displayedPlan?.support_level && (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {formatTitleCase(displayedPlan?.support_level)}
              </li>
            )}
            {displayedPlan?.reporting_level && (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                Reports
              </li>
            )}
            {displayedPlan?.ai_chatbot_availability && (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                AI Chatbot
              </li>
            )}
          </ul>
        </Box>
        {hasPriceUpdate && (
          <Box className="plan-price-update">
            <img src={icons.TermOfServicesInfo} alt="img" />
            <span>
              Your plan price updated from ${activePlan?.price} to $
              {displayedPlan?.price}, Updated price will be applicable from your
              next billing cycle.
            </span>
          </Box>
        )}
        <Box className="custom-plan-buttons">
          {!isShowActiveBadge && !isPending && (
            <button
              onClick={() => onChoosePlan && onChoosePlan(displayedPlan)}
              type="button"
              className={`plan-button ${
                ((displayedPlan?.id == activePlan?.id &&
                  displayedPlan?.plan_type == activePlan?.plan_type) ||
                  (selectedPlan &&
                    displayedPlan?.id === selectedPlan?.id &&
                    displayedPlan?.plan_type === selectedPlan?.plan_type)) &&
                "active-button-plan"
              }`}
            >
              {(displayedPlan?.id == activePlan?.id &&
                displayedPlan?.plan_type == activePlan?.plan_type) ||
              (selectedPlan &&
                displayedPlan?.id === selectedPlan?.id &&
                displayedPlan?.plan_type === selectedPlan?.plan_type)
                ? "Selected"
                : handleButtonText(displayedPlan, activePlan)}
            </button>
          )}

          {showCreateCustomPlanButton && (
            <button className="plan-button" onClick={onCreate} type="button">
              Create Custom Plan
            </button>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default CustomPlanCard;
